import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/utils/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Wallet as WalletIcon, ArrowUpCircle, ArrowDownCircle, Clock, MessageCircle, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Wallet = () => {
  const { user } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [topupAmount, setTopupAmount] = useState(250);
  const [loading, setLoading] = useState(true);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [supportContact, setSupportContact] = useState({ support_whatsapp: "", support_telegram: "" });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [walletRes, plansRes] = await Promise.all([
        api.get("/wallet"),
        api.get("/plans"),
      ]);
      setWalletData(walletRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error("Failed to load wallet data");
    } finally {
      setLoading(false);
    }
  };

  const handleTopupRequest = async (planAmount) => {
    try {
      const response = await api.post("/wallet/manual-topup-request?amount=" + planAmount);
      setSupportContact({
        support_whatsapp: response.data.support_whatsapp || "+91-XXXXXXXXXX",
        support_telegram: response.data.support_telegram || "@uplino_support"
      });
      setShowContactDialog(true);
      toast.success("Top-up request submitted!");
    } catch (error) {
      console.error("Top-up request error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit top-up request");
    }
  };

  const handleWhatsAppContact = () => {
    const whatsappNumber = supportContact.support_whatsapp.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(`Hi, I want to top-up my wallet on uplino.in. Amount: ₹${topupAmount}`);
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    setShowContactDialog(false);
  };

  const handleTelegramContact = () => {
    const telegramId = supportContact.support_telegram.replace('@', '');
    window.open(`https://t.me/${telegramId}`, '_blank');
    setShowContactDialog(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: 'Inter, sans-serif'}}>
            Wallet
          </h1>
          <p className="text-gray-400">Manage your credits and subscription</p>
        </div>

        {/* Wallet Balance */}
        <Card className="p-8 bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-sm border-white/10 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 mb-2">Current Balance</p>
              <p className="text-5xl font-bold text-white">₹{walletData?.balance || 0}</p>
            </div>
            <WalletIcon className="w-16 h-16 text-white/50" />
          </div>
        </Card>

        {/* Active Subscription */}
        {walletData?.subscription && (
          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Active Subscription</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Plan Type</p>
                <p className="text-white font-semibold">{walletData.subscription.plan_type}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Daily Limit</p>
                <p className="text-white font-semibold">{walletData.subscription.daily_topup_limit} posts/bumps</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Expires At</p>
                <p className="text-white font-semibold">
                  {new Date(walletData.subscription.expires_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Plans */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Top-up Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card key={plan.id} className={`p-6 backdrop-blur-sm border-white/10 relative overflow-hidden ${
                plan.bonus_percentage > 0 ? 'bg-gradient-to-br from-green-600/10 to-blue-600/10 border-green-500/30' : 'bg-white/5'
              }`}>
                {plan.bonus_percentage > 0 && (
                  <div className="absolute top-3 right-3">
                    <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full animate-pulse">
                      +{plan.bonus_percentage}% BONUS
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <p className="text-gray-400 text-sm line-through">Pay: ₹{plan.price}</p>
                  {plan.bonus_amount > 0 && (
                    <p className="text-green-400 text-lg font-semibold">
                      Get: ₹{plan.total_credits}
                      <span className="text-sm ml-2">(+₹{plan.bonus_amount} free)</span>
                    </p>
                  )}
                  {plan.bonus_amount === 0 && (
                    <p className="text-white text-3xl font-bold">₹{plan.price}</p>
                  )}
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-start">
                      <span className="text-green-400 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`plan-${plan.id}-button`}
                  onClick={() => {
                    setTopupAmount(plan.price);
                    handleTopupRequest(plan.price);
                  }}
                  className={`w-full text-white ${
                    plan.bonus_percentage > 0 
                      ? 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Request Top-up
                </Button>
              </Card>
            ))}
          </div>
        </div>

        {/* Manual Top-up Instructions */}
        <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">How to Top-up</h2>
          <div className="space-y-3 text-gray-300">
            <p className="flex items-start">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">1</span>
              Select a plan above and click &quot;Request Top-up&quot;
            </p>
            <p className="flex items-start">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">2</span>
              Make payment via UPI to the provided details
            </p>
            <p className="flex items-start">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">3</span>
              Contact support via WhatsApp or Email with your transaction details
            </p>
            <p className="flex items-start">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">4</span>
              Your wallet will be credited within 1-2 hours
            </p>
          </div>
        </Card>

        {/* Transaction History */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">Recent Transactions</h2>
          {walletData?.transactions?.length === 0 ? (
            <Card className="p-12 bg-white/5 backdrop-blur-sm border-white/10 text-center">
              <p className="text-gray-400">No transactions yet</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {walletData?.transactions?.map((transaction) => (
                <Card key={transaction.id} className="p-4 bg-white/5 backdrop-blur-sm border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {transaction.transaction_type === 'credit' ? (
                        <ArrowUpCircle className="w-8 h-8 text-green-400" />
                      ) : (
                        <ArrowDownCircle className="w-8 h-8 text-red-400" />
                      )}
                      <div>
                        <p className="text-white font-semibold">{transaction.description}</p>
                        <p className="text-gray-400 text-sm flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(transaction.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <p className={`text-xl font-bold ${
                      transaction.transaction_type === 'credit' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {transaction.transaction_type === 'credit' ? '+' : '-'}₹{Math.abs(transaction.amount)}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contact Support Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Contact Support for Top-up</DialogTitle>
            <DialogDescription className="text-gray-400">
              Choose how you&apos;d like to contact our support team for your wallet top-up request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-800 rounded-lg">
              <p className="text-white font-semibold mb-2">Top-up Amount: ₹{topupAmount}</p>
              <p className="text-gray-400 text-sm">Please mention this amount when contacting support.</p>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-gray-400 text-xs">
                <strong className="text-white">WhatsApp:</strong> {supportContact.support_whatsapp}
              </p>
              <p className="text-gray-400 text-xs mt-1">
                <strong className="text-white">Telegram:</strong> {supportContact.support_telegram}
              </p>
            </div>
          </div>
          <DialogFooter className="flex gap-3">
            <Button
              onClick={handleWhatsAppContact}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
            <Button
              onClick={handleTelegramContact}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Telegram
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Wallet;
